import Image from "next/image";
import React, { useEffect, useState } from "react";
import { MdSync } from "react-icons/md";
import {
  Button,
  CloseButton,
  Modal,
  ModalBody,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";

import useAlertBar from "@/hooks/useAlertBar";
import { smallLogo } from "@/utils/images";
import {
  verifyRegisterV2,
  resendCodeV2,
  mapAuthError,
} from "@/services/authServiceV2";
import { postLoginHandoff } from "./postLoginHandoff";
import useGoogleAnalytics from "@/hooks/useGoogleAnalytics";

type Props = {
  isOpen: boolean;
  isRegister?: boolean;
  toggle: any;
  userEmail: string;
};

// NEXT-02: registration-only now — v2 forgot-password uses an emailed link, not a code step.
const VerifyCode = ({ isOpen, userEmail, toggle }: Props) => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const { openAlertBar } = useAlertBar();
  const { sendCustomEvent } = useGoogleAnalytics();

  const handleResendCode = async () => {
    setLoading(true);
    try {
      await resendCodeV2({ email: userEmail.toLowerCase() });

      setResendDisabled(true);
      setTimeout(() => setResendDisabled(false), 20000);
      openAlertBar("A new verification code has been sent.", true);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      const { message } = mapAuthError(
        error,
        "Unexpected error occurred while resending your code. Please try again."
      );
      openAlertBar(message, false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    try {
      // v2 /auth/register/verify AUTO-ISSUES a session on success → hand off to the SPA.
      const { data } = await verifyRegisterV2({
        email: userEmail.toLowerCase(),
        code: otp,
      });

      sendCustomEvent("registration", {
        page: {
          url:
            document.location.origin +
            document.location.pathname +
            document.location.search,
        },
        params: { email: userEmail.toLowerCase() },
      });

      postLoginHandoff({
        accessTokenString: data.accessToken,
        accessExpireAt: data.expiresAt,
        refreshTokenString: data.refreshToken,
        refreshExpireAt: "",
      });
    } catch (error) {
      setLoading(false);
      const { message } = mapAuthError(
        error,
        "Unexpected error occurred while verifying your code. Please try again."
      );
      openAlertBar(message, false);
    }
  };

  const handleOtpChange = (event: any, index: any) => {
    if (event.type === "paste") {
      // Split pasted value into individual digits
      const pastedOtp = event.clipboardData.getData("text").slice(0, 6);
      const newOtp = pastedOtp.split("").map((digit: string) => {
        // Check if digit is numeric
        if (!/\d/.test(digit)) {
          return "";
        }
        return digit;
      });
      // Update OTP state with pasted value
      setOtp(newOtp.join("").slice(0, 6));
    } else {
      // Handle digit-by-digit input
      const newOtp = otp.split("");
      if (!/\d/.test(event.target.value)) {
        newOtp[index] = "";
      } else {
        newOtp[index] = event.target.value;
      }
      setOtp(newOtp.join("").slice(0, 6));
    }
  };

  const otpInputs = Array.from({ length: 6 }, (_, index) => (
    <input
      key={index}
      type="text"
      className="form-control "
      style={{
        width: "2.6rem",
        fontSize: "1.5rem",
        fontWeight: "bolder",
      }}
      maxLength={1}
      value={otp[index] || ""}
      onChange={(event) => handleOtpChange(event, index)}
      onPaste={handleOtpChange as any}
    />
  ));

  useEffect(() => {
    const timer = setTimeout(() => setResendDisabled(false), 20000);

    return () => clearInterval(timer);
  }, []);

  return (
    <Modal isOpen={isOpen} backdrop="static" centered toggle={toggle}>
      <ModalBody className="my-2">
        <div className="float-end">
          <CloseButton onClick={toggle} />
        </div>
        <div className="text-center">
          <Image alt="Shipvista Logo" src={smallLogo} width={85} height={85} />
          <h4 className="mb-3">Verify your email address</h4>
          <h6>
            An email with a verification code has been sent to {userEmail}
          </h6>
        </div>
        <div className="d-flex justify-content-evenly my-4">{otpInputs}</div>
        <div className="text-center" style={{ fontSize: 12 }}>
          <p className="text-muted">This code will expire in next 5 minutes </p>
          <UncontrolledTooltip placement="top" target="emailNotReceived">
            If you are having trouble, please check your spam folder, you can
            request a new code, or contact ShipVista customer support for
            further assistance.
          </UncontrolledTooltip>
          <p id="emailNotReceived" className="text-primary">
            Didn&#39;t receive an email?
          </p>
        </div>

        <div className="d-flex justify-content-between">
          {resendDisabled && (
            <UncontrolledTooltip placement="right" target="btnResend">
              Resend will be enabled after 20 seconds
            </UncontrolledTooltip>
          )}

          <div id="btnResend">
            <Button
              disabled={resendDisabled}
              className="ms-3"
              outline
              onClick={handleResendCode}
            >
              RESEND <MdSync />
            </Button>
          </div>
          <Button
            className="me-3"
            disabled={otp.length !== 6 || loading}
            onClick={handleVerify}
          >
            VERIFY
            {loading && <Spinner className="ms-3" size="sm" />}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default VerifyCode;
